import { createRouter, createWebHistory } from "vue-router";
import type { RouteRecordRaw } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { usePlatformStore } from "@/stores/platform";

/**
 * 结构：登录页独立，控制台所有页面都挂在 ConsoleLayout 下面（侧栏 + 顶栏 + 内容区）。
 * 页面标题/副说明由 route.meta 提供，由外壳顶栏统一渲染 ——
 * 业务页里不要再写 <h2>，否则会出现"顶栏一个标题、页面里又一个"。
 *
 * meta.flush: 内容区不吃外层内边距，用于对话这类要自己撑满高度的页面。
 */
const routes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "login",
    component: () => import("@/views/Login.vue"),
    meta: { public: true },
  },
  {
    path: "/",
    component: () => import("@/layouts/ConsoleLayout.vue"),
    meta: { requiresAuth: true },
    children: [
      {
        path: "",
        name: "dashboard",
        component: () => import("@/views/Dashboard.vue"),
        meta: { title: "总览", tip: "租户运营视角" },
      },

      // === Agent ===
      {
        path: "agents",
        name: "agents",
        component: () => import("@/views/agent/AgentList.vue"),
        meta: { title: "智能体", tip: "配置提示词、模型与知识库/技能绑定" },
      },
      {
        path: "agents/new",
        name: "agent-create",
        component: () => import("@/views/agent/AgentForm.vue"),
        meta: { title: "创建智能体", tip: "创建后需发布才能对外服务" },
      },
      {
        path: "agents/:id",
        name: "agent-edit",
        component: () => import("@/views/agent/AgentForm.vue"),
        meta: { title: "编辑智能体" },
      },

      // === Tool ===
      {
        path: "tools",
        name: "tools",
        component: () => import("@/views/tool/ToolList.vue"),
        meta: { title: "技能", tip: "智能体可调用的外部 HTTP 能力" },
      },
      {
        path: "tools/new",
        name: "tool-create",
        component: () => import("@/views/tool/ToolForm.vue"),
        meta: { title: "创建技能" },
      },
      {
        path: "tools/:id",
        name: "tool-edit",
        component: () => import("@/views/tool/ToolForm.vue"),
        meta: { title: "编辑技能" },
      },

      // === Knowledge Base ===
      {
        path: "knowledge-bases",
        name: "knowledge-bases",
        component: () => import("@/views/knowledge-base/KnowledgeBaseList.vue"),
        meta: { title: "知识库", tip: "文档切块向量化，供智能体检索" },
      },
      {
        path: "knowledge-bases/new",
        name: "kb-create",
        component: () => import("@/views/knowledge-base/KnowledgeBaseForm.vue"),
        meta: { title: "创建知识库" },
      },
      {
        path: "knowledge-bases/:id",
        name: "kb-edit",
        component: () => import("@/views/knowledge-base/KnowledgeBaseForm.vue"),
        meta: { title: "编辑知识库" },
      },
      {
        path: "knowledge-bases/:id/documents",
        name: "kb-documents",
        component: () =>
          import("@/views/knowledge-base/KnowledgeBaseDetail.vue"),
        meta: { title: "知识库文档" },
      },

      // === Chat ===
      {
        path: "chat/:agentId?",
        name: "chat",
        component: () => import("@/views/chat/ChatConsole.vue"),
        // agentId 直接作为 prop 传给控制台 (可选参数 —— 缺省时控制台提示补 agentId)
        props: true,
        meta: {
          title: "对话",
          tip: "真实调用模型，会产生 token 消耗",
          flush: true,
        },
      },

      // === 用量报表 (M4) ===
      {
        path: "usage",
        name: "usage",
        component: () => import("@/views/UsageReport.vue"),
        meta: { title: "用量报表", tip: "本租户的 token 消耗明细" },
      },

      // === 租户自己的模型凭据（BYOK）===
      {
        path: "settings/llm",
        name: "llm-credentials",
        component: () => import("@/views/settings/LlmCredentials.vue"),
        meta: {
          title: "模型凭据",
          // 2026-11 起取消平台统一 Key 兜底（纯 BYOK）：不配就聊不了，
          // 这里不能再写"留空则用平台统一 Key"
          tip: "配置本租户自己的模型 Key，不配则无法对话",
        },
      },

      // === 平台管理（跨租户）===
      // ⚠️ 2026-11: 平台管理后台**独立于租户控制台** —— 平台管理员不需要
      //    任何租户账号 (以前是"先登租户账号, 再粘平台密钥", 那等于逼着
      //    平台运维去租户里开一个账号)。现在有独立的 /platform/login。
      {
        path: "admin/tenants",
        redirect: "/platform/tenants",
      },
      {
        path: "admin/tenants/new",
        redirect: "/platform/tenants/new",
      },
      {
        path: "admin/usage",
        redirect: "/platform/usage",
      },
      /*
       * 兜底路由 —— 必须放在**布局内**、且排在最后。
       *
       * 之前没有任何 catch-all：访问不存在的地址（比如手输错、旧链接、后端返回的
       * 带 id 的旧路径）时，router 匹配不到任何记录，整个 <router-view> 渲染为空，
       * 用户看到的是**一片白屏**，既不知道是路径错了，也不知道该点哪里回去。
       * 放在布局内而不是顶层，是为了仍然保留侧栏和顶栏，用户能直接切走。
       */
      {
        path: ":pathMatch(.*)*",
        name: "not-found",
        component: () => import("@/views/NotFound.vue"),
        meta: { title: "页面不存在", tip: "" },
      },
    ],
  },

  // ============================================================
  // 平台管理后台（独立外壳 + 独立登录）
  // ============================================================
  {
    path: "/platform/login",
    name: "platform-login",
    component: () => import("@/views/platform/Login.vue"),
    meta: { public: true },
  },
  {
    path: "/platform",
    component: () => import("@/layouts/PlatformLayout.vue"),
    meta: { requiresPlatformAuth: true },
    children: [
      {
        path: "",
        redirect: "/platform/tenants",
      },
      {
        path: "tenants",
        name: "platform-tenants",
        component: () => import("@/views/admin/TenantList.vue"),
        meta: { title: "租户管理", tip: "平台级操作，需平台管理员账号" },
      },
      {
        path: "tenants/new",
        name: "platform-tenant-create",
        component: () => import("@/views/admin/CreateTenant.vue"),
        meta: { title: "创建租户", tip: "API Key 只在创建时明文展示一次" },
      },
      {
        path: "usage",
        name: "platform-usage",
        component: () => import("@/views/admin/PlatformUsage.vue"),
        meta: { title: "平台用量", tip: "跨租户汇总" },
      },
    ],
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to) => {
  const auth = useAuthStore();
  // ⚠️ 平台后台与租户控制台是**两套登录态** —— 各自判各自的，
  //    别拿租户登录态放行平台页面（那是跨租户权限）。
  if (to.meta.requiresPlatformAuth && !usePlatformStore().isLoggedIn) {
    return { path: "/platform/login", query: { redirect: to.fullPath } };
  }
  if (to.meta.requiresAuth && !auth.isLoggedIn) {
    return { path: "/login", query: { redirect: to.fullPath } };
  }
  if (to.path === "/login" && auth.isLoggedIn) {
    return { path: "/" };
  }
});
